/* ==========================================================================
   Overdue-bill lock.

   A resident with any unpaid bill whose due date has passed can only use the
   Bill Centre and Fund Wallet (so they can pay) until every overdue bill is
   settled. Everything else in the app is hidden and its tabs refuse to open.

   The lock follows the bills in the app store, so it lifts by itself the
   moment the last overdue bill is paid. Buying electricity is ALSO refused
   by the database for such residents (begin_vend), so this is not only cosmetic.
   Only ordinary residents are locked; admins using this app are never held.
   ========================================================================== */

import { config } from "../config";
import { appStore } from "../state/store";
import { sessionStore } from "../state/session";
import { byId } from "./dom";
import { formatNaira } from "./format";

export interface BillLockController {
  isLocked(): boolean;
}

export interface BillLockDeps {
  openBills(): void;
  logout(): void | Promise<void>;
  showDashboard(): void;
}

/** Today's date in Lagos as YYYY-MM-DD, so "overdue" doesn't depend on the phone's own clock/zone. */
function todayInLagos(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Lagos" }).format(new Date());
}

export function initBillLock(deps: BillLockDeps): BillLockController {
  const banner = byId("bill-lock-banner");
  const text = byId("bill-lock-text");
  let locked = false;

  function evaluate(): void {
    const user = sessionStore.user;
    let overdueCount = 0;
    let overdueKobo = 0;

    if (!config.useMockServices && user && user.role === "user" && !appStore.state.loading.bills) {
      const today = todayInLagos();
      const overdue = appStore.state.bills.filter(
        (b) => b.residentId === user.id && b.status !== "paid" && b.dueDate.slice(0, 10) < today
      );
      overdueCount = overdue.length;
      overdueKobo = overdue.reduce((sum, b) => sum + b.amountKobo, 0);
    } else if (appStore.state.loading.bills) {
      return; // still loading: keep whatever state we had rather than flicker
    }

    const shouldLock = overdueCount > 0;
    if (shouldLock) {
      text.textContent =
        `You have ${overdueCount} overdue bill${overdueCount === 1 ? "" : "s"} totalling \u20a6${formatNaira(overdueKobo)}. ` +
        "Please pay to unlock the rest of the app.";
    }
    if (shouldLock === locked) return;
    locked = shouldLock;
    document.body.classList.toggle("bill-locked", locked);
    banner.hidden = !locked;
    if (locked) deps.showDashboard();
  }

  byId("bill-lock-pay-btn").addEventListener("click", () => deps.openBills());
  byId("bill-lock-logout-btn").addEventListener("click", () => {
    void deps.logout();
  });

  appStore.subscribe(evaluate);
  sessionStore.subscribe(() => {
    if (!sessionStore.isAuthenticated && locked) {
      locked = false;
      document.body.classList.remove("bill-locked");
      banner.hidden = true;
    }
  });

  return { isLocked: () => locked };
}
